import {
  DataModel,
  HttpFilter,
  ModelProperty,
  PropertyType,
  SearchManager,
  SearchTag,
  StandardEntity,
} from '@infodata-group/inf-commons';
import { FormComponent } from '@infodata-group/inf-commons-desktop';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { NameValueOperator } from '../engine/name-value-operator';

dayjs.extend(localizedFormat);

export class StandardSearchManager extends SearchManager {
  public get storageKey(): string {
    if (!this.model) {
      return '';
    }
    return `standard-search-${this.model.id}-${this.companyId}`;
  }
  public form: FormComponent;
  public entity = new StandardEntity();
  public model: DataModel;

  public MAX_BY_TAG = 4;
  public EXCLUDING: string[] = ['display'];

  private _disableSavingSearch = false;

  private companyId: string;

  public constructor(
    model: DataModel,
    entity?: StandardEntity,
    disableSavingSearch?: boolean,
  ) {
    super();
    this.model = model;
    this.companyId = JSON.parse(localStorage.getItem('userContext')).ent;
    if (disableSavingSearch) {
      this._disableSavingSearch = disableSavingSearch;
    }
    if (entity) {
      this.entity = entity;
      this._applyPreviousSearch();
      this.generateTags();
    }
  }

  protected generateTags() {
    this.tags = [];
    this.model.properties
      .filter((prop) => this.checkField(prop.id))
      .forEach((prop) => {
        this.tags.push(
          new SearchTag(
            prop.id,
            prop.title,
            this.getValueForTag(prop),
            prop.mandatory,
          ),
        );
      });
  }
  protected getData(): StandardEntity {
    return this.entity;
  }
  protected updateData(tag: SearchTag) {
    console.log(tag);
  }
  protected checkField(field: string): boolean {
    if (this.EXCLUDING.includes(field)) {
      return false;
    }
    return !!this.entity[field];
  }
  protected getValueForTag(prop: ModelProperty): string {
    switch (prop.editType) {
      case 'interval':
        return `${dayjs(this.entity[prop.id].start).format('DD/MM/YYYY')}
        - ${dayjs(this.entity[prop.id].end).format('DD/MM/YYYY')}`;
      case 'switch':
        return this.entity[prop.id] ? 'Oui' : 'Non';
      case 'combo':
        return this.getValueForTagCombo(prop);
      default:
        return this.entity[prop.id];
    }
  }
  public override getFilters(): HttpFilter {
    return new HttpFilter(
      this.getNameValuesOperator().map((x) => x.toNameValue()),
    );
  }
  public getNameValueOperator(property: ModelProperty): NameValueOperator[] {
    const nameValues: NameValueOperator[] = [];
    switch (property.editType) {
      case 'interval':
        nameValues.push(
          new NameValueOperator(
            property.id,
            dayjs(this.entity[property.id].start).format('DD/MM/YYYY'),
            '>',
          ),
        );
        nameValues.push(
          new NameValueOperator(
            property.id,
            dayjs(this.entity[property.id].end).format('DD/MM/YYYY'),
            '<',
          ),
        );
        break;
      case 'combo':
        this.getNameValueOperatorCombo(nameValues, property);
        if (property.multiple) {
          nameValues.push(
            new NameValueOperator(
              `${property.id}.${property.getPrimaryKey()}`,
              this.entity[property.id].map((v) => v[property.getPrimaryKey()]),
            ),
          );
          break;
        }
        nameValues.push(
          new NameValueOperator(
            `${property.id}.${property.getPrimaryKey()}`,
            this.entity[property.id][property.getPrimaryKey()],
          ),
        );
        break;
      default:
        nameValues.push(
          new NameValueOperator(property.id, this.entity[property.id], ''),
        );
        break;
    }
    return nameValues;
  }
  public getNameValuesOperator(): NameValueOperator[] {
    let nameValues: NameValueOperator[] = [];
    for (const entityKey in this.entity) {
      if (!this.checkField(entityKey)) {
        continue;
      }
      const prop = this.model.find(entityKey);
      if (!prop) {
        continue;
      }
      nameValues = [...nameValues, ...this.getNameValueOperator(prop)];
    }
    return nameValues;
  }
  public isFormValid(): boolean {
    return this.form.validate() !== null;
  }
  private _applyPreviousSearch(): void {
    if (this._disableSavingSearch) {
      return;
    }
    const stringObject = sessionStorage.getItem(this.storageKey);
    if (!stringObject) {
      return;
    }
    try {
      const object = JSON.parse(stringObject);
      const entity = new StandardEntity();
      Object.keys(object).forEach((key) => {
        const modelProp = this.model.find(key);
        switch (modelProp.editType) {
          case PropertyType.interval:
            entity[key] = {
              start: new Date(object[key].start),
              end: new Date(object[key].end),
            };
            break;
          default:
            entity[key] = object[key];
            break;
        }
      });
      this.entity = entity;
    } catch (e) {
      // Ignored: an unreadable saved search leaves the current entity
    }
  }

  public saveSearch(): void {
    if (this._disableSavingSearch) {
      return;
    }
    sessionStorage.setItem(
      this.storageKey,
      JSON.stringify(this.entity.serialize()),
    );
  }

  private getNameValueOperatorCombo(
    nameValues: NameValueOperator[],
    property: ModelProperty,
  ): void {
    if (property.multiple) {
      this.getNameValueOperatorComboMultiple(nameValues, property);
      return;
    }
    this.getNameValueOperatorComboSingle(nameValues, property);
  }

  private getNameValueOperatorComboMultiple(
    nameValues: NameValueOperator[],
    property: ModelProperty,
  ): void {
    if (property.getPrimaryKey()) {
      nameValues.push(
        new NameValueOperator(
          `${property.id}.${property.getPrimaryKey()}`,
          this.entity[property.id].map((v) => v[property.getPrimaryKey()]),
        ),
      );
      return;
    }
    nameValues.push(
      new NameValueOperator(`${property.id}`, this.entity[property.id]),
    );
  }

  private getNameValueOperatorComboSingle(
    nameValues: NameValueOperator[],
    property: ModelProperty,
  ) {
    if (property.getPrimaryKey()) {
      nameValues.push(
        new NameValueOperator(
          `${property.id}.${property.getPrimaryKey()}`,
          this.entity[property.id][property.getPrimaryKey()],
        ),
      );
      return;
    }
    nameValues.push(
      new NameValueOperator(`${property.id}`, this.entity[property.id]),
    );
  }

  private getValueForTagCombo(prop: ModelProperty): string {
    const labelsField: string[] = prop.getLabelKey();
    if (!labelsField || labelsField.length === 0) {
      return this.entity[prop.id];
    }
    if (prop.multiple) {
      if (this.entity[prop.id].length > this.MAX_BY_TAG) {
        return `${this.entity[prop.id]
          .map((line) => labelsField.map((label) => line[label]).join(' '))
          .slice(0, this.MAX_BY_TAG)
          .join(', ')} +${this.entity[prop.id].length - this.MAX_BY_TAG}`;
      }
      return `${this.entity[prop.id]
        .map((line) => labelsField.map((label) => line[label]).join(' '))
        .join(', ')}`;
    }
    return labelsField.map((label) => this.entity[prop.id][label]).join(' ');
  }
}
